import type { BuildingViewMode } from "@/stores/ui/buildingViewModeStore";

/**
 * Route ↔ view-mode classification for the building shell.
 *
 * Routes under `/buildings/:id` belong to one of three groups:
 *  - building-only: the index (building dashboard) and the Building Data page,
 *  - projects-only: the project dashboard, the Models routes (3D model and
 *    Drawing Compare), Signing, the Brief, and the PM pages,
 *  - shared: everything else (the Files page, edit) — valid in both modes.
 *
 * Used by the sidebar view-mode toggle (to decide where to navigate on a
 * switch) and by BuildingLayout's deep-link sync (route wins, mode follows).
 */

export function buildingBasePath(buildingId: string): string {
  return `/buildings/${buildingId}`;
}

/** The building dashboard index and the Building Data page — only shown in Building view. */
export function isBuildingOnlyPath(pathname: string, buildingId: string): boolean {
  const base = buildingBasePath(buildingId);
  return pathname === base || pathname === `${base}/data`;
}

/** Project dashboard, the Models routes, Signing, the Brief, and PM pages — only shown in Projects view. */
export function isProjectsOnlyPath(pathname: string, buildingId: string): boolean {
  const base = buildingBasePath(buildingId);
  if (!pathname.startsWith(`${base}/`)) return false;
  return (
    pathname.startsWith(`${base}/project-dashboard`) ||
    pathname.endsWith("/model") ||
    pathname.endsWith("/drawings") ||
    // Signing belongs to a project: a chain is started from a project's files
    // and its container is filed back into that project.
    pathname.endsWith("/signing") ||
    // The project brief (lähteülesanne) is one project's requirements; there
    // is no whole-building reading of it.
    pathname.endsWith("/brief") ||
    pathname.endsWith("/issues") ||
    pathname.endsWith("/my-issues") ||
    pathname.endsWith("/notifications")
  );
}

/** The dashboard a view mode lands on. */
export function dashboardPathFor(mode: BuildingViewMode, buildingId: string): string {
  const base = buildingBasePath(buildingId);
  return mode === "projects" ? `${base}/project-dashboard` : base;
}
